/**
 * Round 19 B25 -- one rule for the word a drawn node reads.
 *
 * The constellation's inspector and hover read a node's word from its
 * open findings and never from its resilience seal: on estate D, 95
 * register rows sealed amber read "Not assessed" and "stable", and 12
 * services sealed red the same. The word now comes from the latest
 * seal; findings add a count beside it but never a state the seal does
 * not hold. Suppliers, channels and configuration items are not sealed
 * by the resilience chain, and say so instead of borrowing a word.
 *
 * The inspector, the hover and the 3D scene all read this.
 */
import type { TwinNode } from "shared/api/types";

import type { NodeVisualState } from "./nodeState";

export type SealWord = "down" | "atRisk" | "stable" | "notAssessed" | "notSealed";

/** Kinds the resilience chain seals. */
const SEALED_KINDS = new Set(["ibs", "service"]);

/** The legacy word, for a reply that predates `resilience`. */
function legacy(state: NodeVisualState, judged: boolean | undefined): SealWord {
  if (state === "down") return "down";
  if (state === "degraded" || state === "at-risk") return "atRisk";
  return judged === false ? "notAssessed" : "stable";
}

export function sealWord(
  node: Pick<TwinNode, "kind" | "judged"> & { resilience?: string | null },
  fallback: NodeVisualState = "stable",
): SealWord {
  if (!SEALED_KINDS.has(node.kind)) return "notSealed";
  if (node.resilience === undefined) return legacy(fallback, node.judged);
  switch (node.resilience) {
    case "red":
      return "down";
    case "amber":
      return "atRisk";
    case "green":
      return "stable";
    default:
      return "notAssessed";
  }
}

/** The dot beside the word: the seal's colour, hollow where none. */
export const SEAL_DOT: Record<SealWord, string | null> = {
  down: "#E2574A",
  atRisk: "#D79A3A",
  stable: "#3E9E6E",
  notAssessed: null,
  notSealed: null,
};
